import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import Highcharts from 'highcharts';
import moment from 'moment';
import { DateRange } from '@shared/utils/date-range';
import { DATE_NOW } from '@shared/services/calendar-data.service';
import { NgStyle } from '@angular/common';
import { HighChartsI18n } from '@shared/components/highcharts/i18n';

export interface ClickEvent {
  category: string;
  slug: any;
}

export type ClickableType = 'column' | 'spline';

@Component({
  selector: 'app-graph',
  template: `<div #chart [ngStyle]="{ 'min-height': height }"></div>`,
  imports: [NgStyle],
})
export class GraphComponent implements AfterViewInit, OnChanges {
  @ViewChild('chart') chart: ElementRef;

  @Input() fontSize: string = '14px';
  @Input() height: string = '400px';
  @Input() data: Highcharts.Options = {};
  @Input() clickableType?: ClickableType;
  @Input() title: string;

  @Output() zoneSelected = new EventEmitter<DateRange>();
  @Output() columnClicked = new EventEmitter<ClickEvent>();
  @Output() isRendered = new EventEmitter<void>();

  constructor(private  readonly i18n: HighChartsI18n) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data && changes.data.currentValue) {
      console.log('ngOnChanges',changes)
      this.drawChart();
    }
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  columnClick = (event: any) => {
    // for unidentified reason, custom for spline in on event.point, and for column on event.point.series.options
    this.columnClicked.emit({
      category: event?.point?.custom?.category ?? event.point.series.options.custom.category,
      slug: event?.point?.custom?.slug ?? event.point.series.options.custom.slug ?? event.point.y,
    });
  };

  drawChart() {
    let options = this.data;
    console.log('drawChart', options, this.chart?.nativeElement)
    if (!this.chart?.nativeElement || !options) return;

    options = this.setGlobalOptions(options);

    Highcharts.chart(options);
  }

  toMoment(x) {
    return moment(Math.round(JSON.parse(JSON.stringify(x))));
  }

  setGlobalOptions(options: Highcharts.Options) {
    if (!options.chart)
      options.chart = {};
    options.chart.renderTo = this.chart.nativeElement;

    options.chart.events = {
      render: () => this.isRendered.emit(),
    };

    options.credits = { enabled: false };

    options.lang = this.i18n.langOptions;

    if (this.title)
      options.title = {
        text: this.title,
        align: 'center',
        y: 20,
        margin: 40
      };

    if (!options.series)
      return options;

    if (options.chart.zooming?.type) {
      options.chart.events.selection = (event) => {
        if (event.xAxis) {
          this.zoneSelected.emit(
            new DateRange(
              this.toMoment(event.xAxis[0].min),
              moment.min([DATE_NOW(), this.toMoment(event.xAxis[0].max)]),
            ),
          );
        }
        return false; // import, disable natural zooming
      };
      options.chart.events.load = drawVerticalBar;
    }

    if (this.clickableType && options.plotOptions?.[this.clickableType]) {
      const plotOptions = options.plotOptions[this.clickableType];
      plotOptions.cursor = 'pointer';
      plotOptions.events = { click: this.columnClick };
    }

    Highcharts.setOptions({ lang: this.i18n.langOptions });

    if (options?.title) {
      options.title.style = {
        ...options.title.style,
        fontSize: this.fontSize,
      };
    }

    return options;
  }
}

function drawVerticalBar() {
  const chart = this;

  // Créer le crosshair en dehors de la zone visible
  chart.customCrosshair = chart.renderer
    .path(['M', -9999, 0, 'L', -9999, chart.plotHeight])
    .attr({
      'stroke-width': 2,
      stroke: '#dbd9da',
      zIndex: 0,
    })
    .add();

  let crosshairInitialized = false; // Pour vérifier si le crosshair a été déjà activé

  Highcharts.addEvent(chart.container, 'mousemove', function (e) {
    const x = chart.pointer.normalize(e).chartX;

    // Vérifier si la souris est dans la zone du graphique
    if (x >= chart.plotLeft && x <= chart.plotLeft + chart.plotWidth) {
      if (!crosshairInitialized) {
        // Initialiser le crosshair lors du premier mouvement
        crosshairInitialized = true;
      }

      // Mettre à jour la position du crosshair
      chart.customCrosshair.attr({
        d: ['M', x, chart.plotTop, 'L', x, chart.plotTop + chart.plotHeight],
      });
    }
  });

  Highcharts.addEvent(chart.container, 'mouseleave', function () {
    // Retirer le crosshair en le déplaçant hors de la zone visible
    chart.customCrosshair.attr({
      d: ['M', -9999, 0, 'L', -9999, 0],
    });
  });
}
